import { useState } from 'react'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { setProfile } from '../../store/authSlice'
import { supabase } from '../../services/supabaseClient'

interface ProfileSetupScreenProps {
  onComplete: () => void
}

export default function ProfileSetupScreen({ onComplete }: ProfileSetupScreenProps) {
  const dispatch = useAppDispatch()
  const profile = useAppSelector((s) => s.auth.profile)
  const [displayName, setDisplayName] = useState(profile?.display_name ?? '')
  const [role, setRole] = useState(profile?.role ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const initials = displayName
    ? displayName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?'

  const handleSave = async () => {
    if (!displayName.trim()) { setError('Please enter your name.'); return }
    if (!profile) return
    setSaving(true)
    setError('')

    const { error: dbError } = await supabase
      .from('profiles')
      .update({ display_name: displayName.trim(), role: role.trim() || null })
      .eq('id', profile.id)

    if (dbError) {
      setError(dbError.message)
      setSaving(false)
      return
    }

    dispatch(setProfile({
      ...profile,
      display_name: displayName.trim(),
      role: role.trim() || null,
    }))
    onComplete()
  }

  return (
    <div className="onboard-wrap" data-testid="profile-setup-screen">
      <div className="onboard-box">
        <h2>Set Up Your Profile</h2>
        <div className="profile-preview" data-testid="profile-preview">
          <div className="user-avatar user-avatar-lg" data-testid="profile-avatar">{initials}</div>
        </div>
        <input
          type="text"
          className="gate-input"
          placeholder="Your name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          data-testid="profile-name-input"
        />
        <input
          type="text"
          className="gate-input"
          placeholder="Job title (optional)"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          data-testid="profile-role-input"
        />
        {error && <div className="gate-err" data-testid="profile-setup-error">{error}</div>}
        <button
          className="btn btn-primary gate-btn"
          disabled={saving || !displayName.trim()}
          onClick={handleSave}
          data-testid="profile-save-btn"
        >
          {saving ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </div>
  )
}
